/**
 * useRewardfull Hook
 * 
 * PURPOSE:
 * Handles Rewardful affiliate tracking integration by updating Stripe customers
 * with referral metadata and notifying the parent frame about conversions.
 * 
 * FLOW:
 * 1. Called from useGuestyReservation after successful reservation
 * 2. Updates Stripe customer via API endpoint (with referral metadata)
 * 3. Posts message to parent frame for Rewardful conversion tracking
 * 4. Listens for acknowledgment from parent frame
 * 
 * KEY COMPONENTS:
 * - createStripeCustomer: Updates existing Stripe customer with referral data
 * - trackConversion: Orchestrates the full conversion tracking process
 * - Message listener: Receives ACK/ERROR responses from parent frame
 * 
 * DEPENDENCIES:
 * - Requires referralId from ClientContext
 * - Requires Stripe customer to exist (created by Guesty)
 * - Requires parent frame with Rewardful script
 */

import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useClient } from "ClientContext";

declare global {
  interface Window {
    rewardful: (action: string, data?: any) => void;
    Rewardful?: any;
    _rwq?: any[];
  }
}

interface StripeCustomerResponse {
  id: string;
  object: string;
  email: string;
  name?: string;
  created: number;
  metadata?: Record<string, string>;
}
const SERVER_URL = process.env.REACT_APP_SERVER_URL;

export function useRewardfull() {

  const { referralId } = useClient();
  const [isUpdatingStripeCustomer, setIsUpdatingStripeCustomer] = useState(false);

  /**
   * Updates an existing Stripe customer with referral metadata
   * 
   * NOTE: This function expects the Stripe customer to already exist.
   * Guesty creates the customer when processing the reservation.
   * We update it afterwards to add referral tracking data.
   * 
   * @param customerData - Customer info and metadata to update
   * @returns Stripe customer object if successful, null otherwise
   */
  const createStripeCustomer = useCallback(async (customerData: {
    email: string;
    name?: string;
    metadata?: Record<string, string>;
  }): Promise<StripeCustomerResponse | null> => {
    try {
      // Call backend API to update Stripe customer
      // The API handles retry logic to wait for Guesty to create the customer
      const response = await axios.post(`${SERVER_URL}/api/updateStripeCustomer`, {
        email: customerData.email,
        name: customerData.name,
        metadata: customerData.metadata,
        referralId: referralId  // Critical for Rewardful tracking
      });

      if (response.status === 200) {
        console.log("[Stripe] ✅ Customer updated successfully:", response.data.customer.id);
        console.log("[Stripe] Customer metadata:", JSON.stringify(response.data.customer.metadata, null, 2));
        return response.data.customer as StripeCustomerResponse;
      }

      return null;

    } catch (error) {
      // Network errors or API failures
      console.error("[Stripe] ❌ Error updating customer:", error);
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          console.warn(`[Stripe] Customer for ${customerData.email} not found; skipping update to avoid Rewardful 422`);
        } else if (error.response) {
          console.error("[Stripe] Error details:", error.response.data);
        }
      }
      return null;
    }
  }, [referralId]);

  /**
   * Starts the loading state for Stripe customer update process
   * Call this immediately when starting the reservation process
   */
  const startStripeUpdate = useCallback(() => {
    if (referralId) {
      setIsUpdatingStripeCustomer(true);
      console.log("[Stripe] Starting reservation and customer update process...");
    }
  }, [referralId]);

  /**
   * Tracks a conversion with Rewardful by:
   * 1. Waiting for Guesty to create the Stripe customer
   * 2. Updating Stripe customer with referral metadata  
   * 3. Notifying parent frame to trigger Rewardful conversion
   * 
   * IMPORTANT: Only runs if a referralId exists (user came through affiliate link)
   * 
   * @param conversionData - Reservation and customer data for tracking
   */
  const trackConversion = useCallback(async (conversionData: {
    email: string;
    firstName?: string;
    lastName?: string;
    externalId?: string;
    saleAmount?: number;
    currency?: string;
    referralId?: string;
  }) => {
    // Skip if no referral - customer didn't come through affiliate link
    if (!referralId) {
      console.log("[Rewardfull] No referral ID found, skipping conversion tracking");
      return;
    }

    // Note: Loading state should already be set by startStripeUpdate
    console.log("[Stripe] Waiting for Guesty to create customer...");

    try {
      // STEP 1: Wait for Guesty to create the Stripe customer
      // Guesty creates customers asynchronously after payment processing
      console.log("[Stripe] Waiting 5 seconds for Guesty to create customer...");
      await new Promise(resolve => setTimeout(resolve, 5000));
      
      // STEP 2: Update Stripe customer with referral metadata
      // This links the Stripe customer to the Rewardful referral
      const fullName = [conversionData.firstName, conversionData.lastName]
        .filter(Boolean)
        .join(' ');

      const stripeCustomer = await createStripeCustomer({
        email: conversionData.email,
        name: fullName || undefined,
        metadata: {
          source: 'rewardfull_conversion',
          external_id: conversionData.externalId || '',
          initial_amount: conversionData.saleAmount?.toString() || '0',
          currency: conversionData.currency || 'USD',
          referral: referralId  // This links to Rewardful
        }
      });

      if (stripeCustomer) {
       // console.log("[Stripe] Customer updated:", stripeCustomer.id);
       // console.log("[Stripe] Customer metadata:", stripeCustomer.metadata);
      } else {
        console.warn("[Stripe] Failed to create customer, continuing with Rewardfull tracking");
      }
    } finally {
      // Always clear loading state when done
      setIsUpdatingStripeCustomer(false);
      console.log("[Stripe] Customer update process completed");
    }

    // STEP 2: Notify parent frame to trigger Rewardful conversion
    // Parent frame has the Rewardful script that will call their API
    try {
      const msg = {
        type: 'CHECKOUT_COMPLETE',
        payload: { email: conversionData.email } // Parent only needs email
      };

      // Post to parent frame (where Rewardful script lives)
      // Using '*' for origin as parent validates the source
      window.top?.postMessage(msg, '*');

      console.log('[REACT APP] → posted CHECKOUT_COMPLETE to top', {
        from: window.location.origin,
        msg
      });
    } catch (e) {
      console.error('[REACT APP] postMessage to top failed:', e);
    }
  }, [createStripeCustomer, referralId]);

  /**
   * Listens for acknowledgment from parent frame after conversion tracking
   * Parent sends REWARDFUL_ACK on success or REWARDFUL_ERROR on failure
   * This helps debug the integration by showing the result in DevTools
   */
  useEffect(() => {
    const onParentReply = (evt: MessageEvent) => {
      const d = evt.data || {};
      if (d.type === "REWARDFUL_ACK") {
        console.log("[REACT APP] ✅ HEAD ACK for:", d.email, d.deduped ? "(deduped)" : "");
      } else if (d.type === "REWARDFUL_ERROR") {
        console.warn("[REACT APP] ⚠️ HEAD error:", d.message);
      }
    };
    window.addEventListener("message", onParentReply);
    return () => window.removeEventListener("message", onParentReply);
  }, []);


  return {
    trackConversion,
    isUpdatingStripeCustomer,
    startStripeUpdate
  };
}